


import React, { useRef, useState, useEffect } from "react"

import { useAuth } from "../contexts/AuthContext"
import { Link, useNavigate } from "react-router-dom"
import CloseIcon from "@mui/icons-material/Close";
import Divider from "@mui/material/Divider";
import Grid from "@mui/material/Grid";
import Modal from "@mui/material/Modal";
import Slide from "@mui/material/Slide";
import { platformAuthenticatorIsAvailable, startAuthentication, browserSupportsWebAuthn, startRegistration } from '@simplewebauthn/browser';
// @mui material components
import Card from "@mui/material/Card";
import CircularProgress from '@mui/material/CircularProgress';
// Material Kit 2 PRO React components
import MKBox from "components/MKBox";
import MKTypography from "components/MKTypography";
import MKInput from "components/MKInput";
import MKButton from "components/MKButton";
import MKAlert from "components/MKAlert";
import { Config } from "../config/Config";
// Authentication pages components
import AuthLayout from "components/Layouts/AuthLayout";
import { jwtDecode } from "jwt-decode";
import Separator from "components/Separator";

function SignIn() {

  const { login, loginComplete, addPasskey, addPasskeyComplete, isLoggedIn, socialLogin, socialLoginComplete, socialSignup, socialSignupComplete } = useAuth()
  const [error, setError] = useState("")
  const [message, setMessage] = useState("")
  const [loading, setLoading] = useState(false)
  const [isAddingNewPasskey, setIsAddingNewPasskey] = useState(false)
  const [data, setData] = useState({})
  const [challengeData, setChallengeData] = useState({})
  const appleLoginSuccess = useRef(false)
  const userData = useRef({})
  const navigate = useNavigate()

  let config = process.env.REACT_APP_SERVER ===  'dev' ? Config.DEV : process.env.REACT_APP_SERVER ===  'prod' ? Config.PROD : Config.LOCAL;

  useEffect(() => {
    if (!browserSupportsWebAuthn()) {
      setError('It seems this browser does not support Passkey Authentication.');
      return;
    } 

    if (isLoggedIn()) { 
      navigate("/apps")
      return;
    }

    const appleIDSignInOnFailure = (event) => {
      console.log("AppleIDSignInOnFailure.....", event.detail);
      setError(`Failed to log in: ${event.detail.error}`);
    }

    const appleIDSignInOnSuccess = (event) => {
      // Listen for authorization success.
      // Handle successful response.
      console.log("appleIDSignInOnSuccess ... ");

      // this event run twice for some reason. need only run once
      if (userData.current.token) return;
      

      console.log("appleIDSignInOnSuccess .event.detail.authorization.. ", event.detail.authorization);
      console.log("appleIDSignInOnSuccess .event.detail.user.. ", event.detail.user);

      if (event.detail.authorization) {

        let id_token = event.detail.authorization.id_token; 
        let user = event.detail.user;

        if (user) {
          userData.current = {
            'token': id_token,
            'email': user.email,
            'firstName': user.name.firstName,
            'lastName': user.name.lastName,
          }

        }
        else {

          userData.current = {
            'token': id_token,
          } 
        } 
        submitSocialLogin(id_token, "apple");
      }
      else setError("Failed to log in");
    }


    /* global google */
    if (window.google) {
      google.accounts.id.initialize({
        client_id: config.GOOGLE_CLIENT_ID,
        callback: handleGoogle,
      });

      google.accounts.id.renderButton(document.getElementById("googleDiv"), {
        type: "standard",
        theme: "filled_blue",
        size: "large",
        width: "200",
        text: "Sign in with Google",
        shape: "pill",
      }); 
    }

    
    window.AppleID.auth.init({
      clientId: config.APPLE_BUNDLE_ID,
      scope: 'email name',
      redirectURI: config.APPLE_REDIRECT_URI,
      state: 'SignInUserAuthenticationRequest',
      usePopup: true
    });


    document.addEventListener('AppleIDSignInOnSuccess', appleIDSignInOnSuccess) 
    document.addEventListener('AppleIDSignInOnFailure', appleIDSignInOnFailure)


    return () => {
      document.removeEventListener('AppleIDSignInOnFailure', appleIDSignInOnFailure);
      document.removeEventListener('AppleIDSignInOnSuccess', appleIDSignInOnSuccess);
    };

    // eslint-disable-next-line 
  }, [])


  

  async function handleGoogle(response) {
    console.log("handleGoogle response ", response); 

    if (response.credential){

      let decoded = await jwtDecode(response.credential);

      //console.log("handleGoogle decoded ", decoded);
      userData.current = {
        'token': response.credential,
        'email': decoded.email,
        'firstName': decoded.given_name,
        'lastName': decoded.family_name
      } 

      submitSocialLogin(response.credential, 'google');
    } 
    else setError("Failed to log in")
  };

 

  
  async function submitSocialLogin(token, provider) {
    try {
      setError("")
      setLoading(true)
      const res = await socialLogin(token, provider);

      console.log("submitSocialLogin result ", res)

      if (res.error) {
       
        if (res.error.code === 603) {

          if (provider === 'apple' && !userData.current.firstName) {
            setError(`Whoop! Invalid sign up data. Please remove AppKeyID in 'Sign in with Apple' from your icloud setting and try again.`)
            return;
          }
          console.log("submitSocialLogin userData ", userData.current)

          let challengeData  = await socialSignup(token, userData.current.email, provider, userData.current.firstName, userData.current.lastName)
          if (challengeData.error) {
            if (challengeData.error.message) setError(`Whoop! ${challengeData.error.message}`)
            else setError(`Whoop! ${challengeData.error}`)
          }
          else {
            singupProcess(challengeData);
          }

        }
        else if (res.error.message) setError(`Whoop! ${res.error.message}`)
        else setError(`Whoop! ${res.error}`)

      }
      else {

        if (res.requireAddPasskey) {
          setIsAddingNewPasskey(true)
          return;
        }
    
        setChallengeData(res)
    
        if (res.isOpeningAddNewPasskey) {
          setIsAddingNewPasskey(true)
        }
        else {
          challengeLogin(res, provider); 
        }
      }
    } catch (error) {
      console.log("submitSocialLogin error ", error);
      setError("Failed to log in")
    }
    finally {
      setLoading(false)
    }
  }


 
  const onKeyEnterDown = (event) => {
    if (event.keyCode == 13) getLoginChallenge()
  }

  const getLoginChallenge = async () => {
    try {

      setError()
      setChallengeData()

      if (!await platformAuthenticatorIsAvailable()) {
        setError("Your device doesn't have Passkey Authenticator. Please use any security key device to register.")
        return;
      }

      setLoading(true)


      if (data.email === undefined || data.email === "") {

        setError("Please enter an email")
        return;
      }

      let result = await login(data.email);
      if (result.error) {
        setError(result.error.message)
        return
      }
      if (result.requireAddPasskey) {
        setIsAddingNewPasskey(true)
        return;
      }

      setChallengeData(result)

      if (result.isOpeningAddNewPasskey) {
        setIsAddingNewPasskey(true)
      }
      else {
        challengeLogin(result);
        setLoading(false)
      }



    } catch (error) {
      console.log("getRegisterChallenge error ", error);
      if (error.message) {
        setError(error.message)
      }

    }
    finally {
      setLoading(false)
    }
  }

  const singupProcess = async(challengeData) => {
    if (!browserSupportsWebAuthn()) {
      setError('It seems this browser does not support Passkey Authentication.');
      return;
    }
    try { 
      
      let attResp = await startRegistration({ optionsJSON : challengeData});
      attResp.email = challengeData.user.email;
      
      setLoading(true)
      
      let result = await socialSignupComplete(attResp);  
      
      if (result.error) {
        setError(result.error.message)
        return
      }

      navigate("/apps")

    } catch (error) {
      console.log("socialSignupComplete error ", error)

      setLoading(false)

      setError("Invalid Request")
    }
  }

  const challengeLogin = async (ch, provider) => {
    try { 

      ch = ch ? ch : challengeData;
      if (!ch) return;
      let asseResp = await startAuthentication({ optionsJSON: ch });
      asseResp.email = ch.email;

      let authn; 
      if (provider) authn = await socialLoginComplete(asseResp);
      else authn = await loginComplete(asseResp);
      
      if (authn.error) {
        setError(authn.error.message)
        userData.current = {}
      }
      else {
        navigate("/apps")
      }
    } catch (error) {
      console.log("startAuthentication ", error)
    }

  }

  const onChangeValue = async (evt) => {

    setData({
      ...data,
      [evt.target.name]: evt.target.value
    })


  }


  const confirmAddPasskey = async () => {
    try {


      if (data.passkeyToken && data.passkeyToken !== "") {
        let result = await addPasskey(data.email, data.passkeyToken)
        if (result.error) {
          setError(result.error.message)
          return
        }

        let attResp = await startRegistration({ optionsJSON : result});

        console.log("confirmAddPasskey startRegistration attResp =  ", attResp)

        attResp.email = data.email;

        let user = await addPasskeyComplete(attResp, data.passkeyToken);
        if (user.error) {
          setError(user.error.message)
        }
        else {
          setIsAddingNewPasskey(false)
          getLoginChallenge()
          setMessage("Success! Please login with your new passkey.");
        }
        console.log("confirmAddPasskey user =  ", user)

      }
      else {
        setError("Please enter passkey token")
      }

    } catch (error) {
      setError(error.message)
    }
  }


  return (
    <AuthLayout>
      <Card>
        <MKBox
          variant="contained"
          bgColor="primary"
          borderRadius="lg"
          coloredShadow="info"
          mx={2}
          mt={-3}
          p={2}
          mb={1}
          textAlign="center"
        >
          <MKTypography variant="h4" fontWeight="medium" color="white" mt={1}>
            Sign in
          </MKTypography>

        </MKBox>

        {error &&
          <MKAlert color="error">
            Whoop!&nbsp; {error}
          </MKAlert>
        }

        {message &&
          <MKAlert color="info">
            {message}
          </MKAlert>
        }

        {isAddingNewPasskey ?
          <MKBox pt={4} pb={3} px={3}>

          </MKBox>

          :


          <MKBox pt={4} pb={3} px={3}>

            <MKBox  >
              <MKBox mb={2} autoComplete="off" action={getLoginChallenge}>

                <MKInput label="Email" name="email" fullWidth onChange={onChangeValue} 
                inputProps={{ autoComplete: "off", autoCapitalize: "none", style: { textTransform: "lowercase" } }}
                autoFocus required autoComplete="new-password" onKeyDown={onKeyEnterDown} />

              </MKBox>

              <MKBox mt={4} mb={1} textAlign="center">
                {loading ?
                  <CircularProgress color="primary" />
                  :
                  <MKBox mb={1}>
                    <MKButton variant="contained" color="primary" fullWidth onClick={getLoginChallenge}>
                      sign in
                    </MKButton>
                  </MKBox>
                }
                <Separator />
                <div className="row mb-3">
                  <div className="col"></div>
                  <div className="col">
                    <div id="googleDiv" ></div>
                  </div>
                  <div className="col"></div>
                </div>

                <div className="row"> 
                  <div className="col"></div>
                  <div className="col">
                    <div id="appleid-signin"
                      data-mode="center-align"
                      data-type="sign-in"
                      data-color="white"
                      data-border="true"
                      data-border-radius="50"
                      data-width="200"
                      data-height="40"
                      className="social-button"></div>
                  </div>
                  <div className="col"></div> 
                </div>

              </MKBox>


              <MKBox mt={3} mb={1} textAlign="center">

                <MKTypography variant="button" color="text">
                  Don&apos;t have an account?{" "}
                  <MKTypography
                    component={Link}
                    to="/signup"
                    variant="button"
                    color="info"
                    fontWeight="medium"
                    textGradient
                  >
                    Sign up
                  </MKTypography>
                </MKTypography>
              </MKBox>

            </MKBox>

          </MKBox>

        }
      </Card>




      <Modal open={isAddingNewPasskey} onClose={() => setIsAddingNewPasskey(false)} sx={{ display: "grid", placeItems: "center" }}>
        <Slide direction="down" in={isAddingNewPasskey} timeout={300}>
          <Grid container item xs={12} lg={7} justifyContent="center" mx="auto">
            <MKBox
              sx={{ width: '95%' }}
              display="flex"
              flexDirection="column"
              borderRadius="xl"
              borderWidth={2}
              bgColor="white"
              shadow="xl"
              textAlign="center"
            >


              <MKBox display="flex" alignItems="center" justifyContent="space-between" p={2}>
                <MKTypography variant="h5" color="info">Your Account is opening to add new Passkey</MKTypography>
                <CloseIcon fontSize="medium" sx={{ cursor: "pointer" }} onClick={() => setIsAddingNewPasskey(false)} />
              </MKBox>

              <Grid container item xs={12} lg={8} sx={{ mx: "auto" }}>
                <MKBox width="100%" component="form" method="post" autocomplete="off">
                  <MKBox p={3}>

                    {error &&
                      <MKAlert color="error">
                        Whoop!&nbsp; {error}
                      </MKAlert>
                    }

                    <MKTypography variant="h5">Please enter Passkey token to add new passkey.</MKTypography>

                    <MKInput multiline maxRows={4} label="Passkey Token" name="passkeyToken" fullWidth required variant="standard" onChange={onChangeValue} />

                  </MKBox>

                </MKBox>
              </Grid>

              <Divider sx={{ my: 0 }} />

              <MKBox display="flex" alignItems="center" justifyContent="space-between" p={2}>
                <MKTypography variant="h5">Click Login button to continue to login.</MKTypography>
              </MKBox>


              <MKBox display="flex" justifyContent="space-between" p={1.5}>
                <MKButton variant="gradient" color="dark" onClick={() => { setIsAddingNewPasskey(false); challengeLogin(challengeData) }}>
                  Login
                </MKButton>

                <MKButton type="submit" variant="gradient" color="primary" onClick={confirmAddPasskey}>
                  Submit
                </MKButton>

              </MKBox>

            </MKBox>
          </Grid>
        </Slide>
      </Modal>


    </AuthLayout>
  );
}

export default SignIn;
